import { useCallback } from 'react';
import { PermissionSetAssignment, PortalUserData } from '../../../types';
import { useFeatureFlags } from '../../../Api/useFeatureFlags';
import { useSnackbar } from '../../../Context/SnackbarContext';
import {
  assignPermissionSet,
  unassignPermissionSet,
  usePermissionSetAssignments,
  usePermissionSets
} from '../../../store/PermissionSets';
import { DetailViewCard } from '../CardWithTitle';
import { PermissionSetAssignmentRow } from './PermissionSetAssignmentRow';
import { assignmentForTeam, assignmentForUser, userAssignmentAccounts } from './userAssignmentHelpers';

export interface UserDrawerPermissionSetsSectionProps {
  userData: PortalUserData;
}

export const UserDrawerPermissionSetsSection = ({ userData }: UserDrawerPermissionSetsSectionProps) => {
  const { config } = useFeatureFlags();
  if (!config.permission_sets) return null;
  return <UserDrawerPermissionSets userData={userData} />;
};

const UserDrawerPermissionSets = ({ userData }: UserDrawerPermissionSetsSectionProps) => {
  const { setSnack } = useSnackbar();
  const { permissionSets } = usePermissionSets();
  const { assignments, submitting } = usePermissionSetAssignments();

  const handleChange = useCallback(
    async (accountId: string, current: PermissionSetAssignment | null, value: string) => {
      const result = value
        ? await assignPermissionSet({
            permission_set_id: value,
            portal_user_id: Number(userData.id),
            account_id: accountId
          })
        : current
          ? await unassignPermissionSet(current.id)
          : null;
      if (result?.error)
        setSnack({ message: result.error.message || 'Could not update permission set', type: 'error', open: true });
    },
    [userData.id, setSnack]
  );

  const teamInherited = userData.team_id != null ? assignmentForTeam(assignments, String(userData.team_id)) : undefined;

  return (
    <DetailViewCard
      key="admin_permission_sets"
      title="Permission Sets">
      {userAssignmentAccounts(userData, assignments).map((account) => {
        const current = assignmentForUser(assignments, account.id, userData.id);
        return (
          <PermissionSetAssignmentRow
            key={account.id}
            label={account.name || account.id}
            value={current?.permission_set_id ?? ''}
            permissionSets={permissionSets}
            emptyLabel="None — role governs"
            ariaLabel={`${account.name || account.id} permission set`}
            canEdit
            submitting={submitting}
            onChange={(value) => handleChange(account.id, current, value)}
          />
        );
      })}
      {teamInherited !== undefined && (
        <PermissionSetAssignmentRow
          label="Via team"
          value={teamInherited?.permission_set_id ?? ''}
          permissionSets={permissionSets}
          emptyLabel="None"
          ariaLabel="Team permission set"
          canEdit={false}
        />
      )}
    </DetailViewCard>
  );
};
